import {useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {InformationCircleIcon, MagnifyingGlassIcon, PlusIcon, UsersIcon} from '@heroicons/react/24/outline'
import Shell from "../../../../../components/specific/Shell.tsx";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import {fieldVariants} from "../../../../../components/general/fieldStyles.ts";
import {cn} from "../../../../../lib/utils.ts";
import {User} from "../../../../../api/accessControl/User.ts";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {useCustomers} from "../../../../../hooks/useCustomers.ts";
import {Customer, CustomerDraft, customerMatchesSearch} from "../../../../../api/customers/Customer.ts";
import CustomerCard from "./CustomerCard.tsx";
import CustomerFormModal from "../../../../../components/specific/CustomerFormModal.tsx";

export default function CustomersPage({currentUser}: { currentUser: User }) {

    const navigate = useNavigate()
    const {dispatch: dispatchNotification} = useNotifications()
    const {customers, isLoading, loadError, createCustomer, changeCustomer, archiveCustomer} = useCustomers(currentUser)

    const [searchQuery, setSearchQuery] = useState('')
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [drawerSession, setDrawerSession] = useState(0)
    const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null)
    const [isSaving, setIsSaving] = useState(false)

    const filteredCustomers = useMemo(
        () => customers.filter(c => customerMatchesSearch(c, searchQuery)),
        [customers, searchQuery],
    )

    function openCreateDrawer() {
        setEditingCustomer(null)
        setDrawerSession(session => session + 1)
        setIsDrawerOpen(true)
    }

    function openEditDrawer(customer: Customer) {
        setEditingCustomer(customer)
        setDrawerSession(session => session + 1)
        setIsDrawerOpen(true)
    }

    async function handleSave(draft: CustomerDraft) {
        setIsSaving(true)
        try {
            if (editingCustomer) {
                await changeCustomer(editingCustomer.id, draft)
                dispatchNotification({
                    title: 'Klant bijgewerkt',
                    description: `${draft.company_name} is opgeslagen.`,
                    type: 'success',
                })
            } else {
                await createCustomer(draft)
                dispatchNotification({
                    title: 'Klant toegevoegd',
                    description: `${draft.company_name} is toegevoegd aan je klanten.`,
                    type: 'success',
                })
            }
            setIsDrawerOpen(false)
        } catch (error) {
            console.error('Error saving customer:', error)
            dispatchNotification({
                title: 'Opslaan mislukt',
                description: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het opslaan van de klant.',
                type: 'error',
            })
        } finally {
            setIsSaving(false)
        }
    }

    async function handleArchive(customer: Customer) {
        try {
            await archiveCustomer(customer.id)
            setIsDrawerOpen(false)
            dispatchNotification({
                title: 'Klant verwijderd',
                description: `${customer.company_name} is verwijderd.`,
                type: 'success',
            })
        } catch (error) {
            console.error('Error archiving customer:', error)
            dispatchNotification({
                title: 'Verwijderen mislukt',
                description: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het verwijderen van de klant.',
                type: 'error',
            })
        }
    }

    function handleCreateInvoice(customer: Customer) {
        navigate(`/create-invoice?customer=${customer.id}`)
    }

    const hasCustomers = customers.length > 0

    return (
        <Shell
            currentUser={currentUser}
            isLoading={isLoading}
            title="Klanten"
            subtitle="Opgeslagen klantgegevens — automatisch ingevuld bij nieuwe facturen."
            titleActions={hasCustomers
                ? <PrimaryButton title="Nieuwe klant" icon={PlusIcon} onClick={openCreateDrawer}/>
                : null}
        >

            {loadError && (
                <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {loadError}
                </div>
            )}

            {hasCustomers && (
                <div className="mb-6 flex items-center justify-between gap-4">
                    <div className="relative w-full max-w-sm">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"/>
                        <input
                            type="search"
                            className={cn(fieldVariants(), "pl-9")}
                            placeholder="Zoek op naam, plaats, e-mail of btw-nummer"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <span className="shrink-0 text-sm text-gray-500">
                        {filteredCustomers.length} van {customers.length} klanten
                    </span>
                </div>
            )}

            {!hasCustomers && !isLoading && (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 px-6 py-20 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                        <UsersIcon className="h-7 w-7 text-gray-400"/>
                    </div>
                    <h2 className="mt-4 text-lg font-semibold text-gray-900">Nog geen klanten</h2>
                    <p className="mt-1 max-w-md text-sm text-gray-600">
                        Voeg je klanten één keer toe en hun gegevens staan voortaan automatisch op elke factuur.
                    </p>
                    <div className="mt-6">
                        <PrimaryButton title="Eerste klant toevoegen" icon={PlusIcon} onClick={openCreateDrawer}/>
                    </div>
                </div>
            )}

            {hasCustomers && filteredCustomers.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 px-6 py-16 text-center">
                    <h2 className="text-base font-semibold text-gray-900">Geen klanten gevonden</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Geen resultaten voor "{searchQuery}".
                    </p>
                    <div className="mt-4">
                        <SecondaryButton title="Zoekopdracht wissen" onClick={() => setSearchQuery('')}/>
                    </div>
                </div>
            )}

            {filteredCustomers.length > 0 && (
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 2xl:grid-cols-3">
                    {filteredCustomers.map(customer => (
                        <CustomerCard
                            key={customer.id}
                            customer={customer}
                            onEdit={openEditDrawer}
                            onCreateInvoice={handleCreateInvoice}
                        />
                    ))}
                </div>
            )}

            <CustomerFormModal
                open={isDrawerOpen}
                sessionKey={drawerSession}
                customer={editingCustomer}
                isSaving={isSaving}
                onClose={() => setIsDrawerOpen(false)}
                onSave={handleSave}
                onArchive={handleArchive}
            />
        </Shell>
    )
}
